import React, { useState } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Alert, ButtonGroup } from 'react-bootstrap';
import { useUsers } from '@hooks';
import { useCourses } from '@hooks';

/**
 * User Manager Component
 * Handles CRUD operations for users
 */
export function UserManager({ onViewProgress }) {
  const { users, loading, error, createUser, updateUser, deleteUser, setPassword } = useUsers();
  const { courses } = useCourses();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'student',
    managedCourses: [],
    isActive: true,
  });
  const [formError, setFormError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);

  /**
   * Handle opening create modal
   */
  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'student',
      managedCourses: [],
      isActive: true,
    });
    setFormError(null);
    setShowModal(true);
  };

  /**
   * Handle opening edit modal
   */
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      managedCourses: user.managedCourses || [],
      isActive: user.isActive,
    });
    setFormError(null);
    setShowModal(true);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setFormError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setFormError('Invalid email format');
      return;
    }

    try {
      const payload = {
        ...formData,
        managedCourses: formData.role === 'course_manager' ? (formData.managedCourses || []) : [],
      };

      if (editingUser) {
        await updateUser(editingUser.id, payload);
      } else {
        await createUser(payload);
      }
      setShowModal(false);
    } catch (err) {
      setFormError(err.message);
    }
  };

  /**
   * Handle user deletion
   */
  const handleDelete = async (user) => {
    if (window.confirm(`Are you sure you want to delete ${user.name}? This will remove all their progress data.`)) {
      try {
        await deleteUser(user.id);
      } catch (err) {
        alert(`Error deleting user: ${err.message}`);
      }
    }
  };

  /**
   * Handle opening password reset modal
   */
  const handleResetPassword = (user) => {
    setPasswordResetUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setShowPasswordModal(true);
  };

  /**
   * Handle password reset submission
   */
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Validation
    if (!newPassword) {
      setPasswordError('Password is required');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      await setPassword(passwordResetUser.id, newPassword);
      setShowPasswordModal(false);
      alert(`Password successfully reset for ${passwordResetUser.name}`);
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  /**
   * Get role badge variant
   */
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
        return 'danger';
      case 'course_manager':
        return 'warning';
      default:
        return 'primary';
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading users...</div>;
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">User Management</h5>
          <Button variant="primary" size="sm" onClick={handleCreate}>
            + Add User
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {users.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No users found. Create your first user to get started.
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Enrolled Courses</th>
                  <th>Managed Courses</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <Badge bg={getRoleBadgeVariant(user.role)}>
                        {user.role}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={user.isActive ? 'success' : 'secondary'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>{user.enrolledCourses.length}</td>
                    <td>{(user.managedCourses || []).length}</td>
                    <td>
                      {user.lastLoginAt 
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        <Button 
                          variant="outline-primary" 
                          onClick={() => handleEdit(user)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline-warning" 
                          onClick={() => handleResetPassword(user)}
                          title="Reset Password"
                        >
                          🔑
                        </Button>
                        <Button 
                          variant="outline-info" 
                          onClick={() => onViewProgress(user)}
                        >
                          Progress
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          onClick={() => handleDelete(user)}
                        >
                          Delete
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* User Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingUser ? 'Edit User' : 'Create New User'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter user's full name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="student">Student</option>
                <option value="course_manager">Course Manager</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>

            {formData.role === 'course_manager' && (
              <Form.Group className="mb-3">
                <Form.Label>Assigned Courses</Form.Label>
                <Form.Select
                  multiple
                  value={formData.managedCourses}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData({ ...formData, managedCourses: selected });
                  }}
                >
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Hold Ctrl (Cmd on Mac) to select multiple courses.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Form onSubmit={handlePasswordSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              Reset Password for {passwordResetUser?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {passwordError && <Alert variant="danger">{passwordError}</Alert>}
            
            <Alert variant="info">
              <strong>Note:</strong> Setting a new password will allow the user to log in with this password.
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>New Password *</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                autoComplete="new-password"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password *</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Reset Password
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
